import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../lib/api';
import { useAuthStore } from './authStore';

interface Permission {
  module_name: string;
  resource_name: string;
  action: string;
  scope: string;
}

/** Roles that see every People screen. Mirrors FULL_ACCESS_ROLES in the People service. */
const FULL_ACCESS_ROLES = ['Super Admin', 'Reporting Manager', 'HR Admin'];

/**
 * Modules whose screens follow their grants strictly (migration 42): only Super
 * Admin bypasses them. The People full-access roles do not reach into Support
 * or Assets — e.g. HR Admin must not see Support webhooks or email settings.
 * Attendance too: a Reporting Manager has Team scope and no Settings, exactly as
 * granted (matches requireGrant in the People service).
 */
const STRICT_MODULES = ['Support', 'Assets', 'Attendance', 'Admin'];

const isSuperRole = (roles: string[]) => {
  const norm = (roles || []).map((r) => String(r || '').toLowerCase().replace(/[_\s-]+/g, ''));
  if (norm.some((r) => r === 'superadmin' || r === 'platformadmin' || r === 'admin')) return true;
  try {
    const authUser = useAuthStore.getState().user;
    if (authUser?.role === 'admin' || authUser?.role === 'super_admin') return true;
  } catch (e) {
    // ignore if store not initialized
  }
  return false;
};

const bypasses = (roles: string[], moduleName: string) =>
  isSuperRole(roles) ||
  (!STRICT_MODULES.includes(moduleName) && roles.some((r) => FULL_ACCESS_ROLES.includes(r)));

interface RbacState {
  permissions: Permission[];
  roles: string[];
  /** Top-level hubs this user may see (keys from config/hubs.ts), from /auth/me. */
  hubs: string[];
  isLoading: boolean;
  isLoaded: boolean;
  /** True when the last fetch failed, so callers can tell "denied" from "unknown". */
  loadFailed: boolean;

  hasPermission: (moduleName: string, resourceName: string, action: string) => boolean;
  getScope: (moduleName: string, resourceName: string, action: string) => string | null;
  hasRole: (role: string) => boolean;
  hasFullAccess: () => boolean;
  /** Whether this user may see a top-level hub (module). Super Admin always can. */
  hasHub: (hubKey: string) => boolean;
  /** False when permissions could not be determined (never loaded, or the fetch failed). */
  isPermissionDataAvailable: () => boolean;
  loadPermissions: () => Promise<void>;
  reloadPermissions: () => Promise<void>;
  clearPermissions: () => void;
}

export const useRbacStore = create<RbacState>()(
  persist(
    (set, get) => ({
      permissions: [],
      roles: [],
      hubs: [],
      isLoading: false,
      isLoaded: false,
      loadFailed: false,

      hasPermission: (moduleName, resourceName, action) => {
        const state = get();
        if (bypasses(state.roles, moduleName)) return true;
        return state.permissions.some(p =>
          p.module_name === moduleName &&
          p.resource_name === resourceName &&
          p.action === action
        );
      },

      hasFullAccess: () => isSuperRole(get().roles) || get().roles.some((r) => FULL_ACCESS_ROLES.includes(r)),

      /**
       * Whether this user may see a top-level hub. Super Admin always may (so it
       * can reach unpublished hubs). The server never returns an empty hub list
       * (it falls back to People), so an empty list here means hub data is
       * unknown — never loaded, the fetch failed, or an older backend that does
       * not send hubs yet. In that case we fail SAFE to the published baseline
       * (People only) rather than exposing every hub or hiding the one live hub.
       */
      hasHub: (hubKey) => {
        const s = get();
        if (s.roles.includes('Super Admin')) return true;
        if (s.hubs.length === 0) return hubKey === 'people';
        return s.hubs.includes(hubKey);
      },

      /**
       * Whether we actually know what this user may do. If permissions were
       * never loaded or the fetch failed, callers should not treat "no
       * permission" as "denied" — the API is the real authority.
       */
      isPermissionDataAvailable: () => {
        const s = get();
        return s.isLoaded && !s.loadFailed && (s.permissions.length > 0 || s.roles.length > 0);
      },

      getScope: (moduleName, resourceName, action) => {
        const state = get();
        if (bypasses(state.roles, moduleName)) return 'Company';
        // A user can hold the same permission through several roles; the
        // broadest scope wins (Company over Self).
        const scopes = state.permissions
          .filter((p) => p.module_name === moduleName && p.resource_name === resourceName && p.action === action)
          .map((p) => (p.scope === 'Company' || p.scope === 'Team' || p.scope === 'Department' ? 'Company' : p.scope));
        if (scopes.includes('Company')) return 'Company';
        return scopes[0] ?? null;
      },

      hasRole: (role) => {
        return get().roles.includes(role);
      },

      loadPermissions: async () => {
        if (get().isLoading || get().isLoaded) return;
        set({ isLoading: true, loadFailed: false });
        try {
          // /auth/me returns { user, roles, permissions }
          const response = await apiClient.get('/auth/me');
          const roles = response.data.roles || [];
          set({
            permissions: response.data.permissions || [],
            roles: roles,
            hubs: response.data.hubs || [],
            isLoading: false,
            isLoaded: true,
            loadFailed: false,
          });

          // Sync roles into authStore so all modules have immediate access
          try {
            const authUser = useAuthStore.getState().user;
            if (authUser && roles.length > 0) {
              useAuthStore.setState({
                user: {
                  ...authUser,
                  roles: Array.from(new Set([...(authUser.roles || []), ...roles])),
                },
              });
            }
          } catch (e) {
            // ignore if authStore not yet ready
          }
        } catch (error) {
          // Do NOT wipe previously cached permissions here. Clearing them on a
          // transient failure (auth service down, network blip) used to leave
          // the app believing the user has no access at all, which silently
          // emptied the navigation.
          //
          // isLoaded is set so the effect in ProtectedRoute does not retry in a
          // loop, but it is deliberately not persisted, so the next page load
          // fetches again rather than caching a failure forever.
          console.error('Failed to load permissions', error);
          set({ isLoading: false, isLoaded: true, loadFailed: true });
        }
      },

      /** Force a refetch, e.g. after the API becomes reachable again. */
      reloadPermissions: async () => {
        set({ isLoaded: false, isLoading: false });
        await get().loadPermissions();
      },

      clearPermissions: () => {
        set({ permissions: [], roles: [], hubs: [], isLoaded: false, loadFailed: false });
      }
    }),
    {
      name: 'rbac-storage',
      // Persist only the data, never the load flags. Persisting `isLoaded`
      // made loadPermissions() short-circuit forever on later visits, so a
      // single failed fetch was cached permanently.
      partialize: (state) => ({
        permissions: state.permissions,
        roles: state.roles,
        hubs: state.hubs,
      }),
      // Bumped when the persisted shape changed. Existing browsers still hold
      // an entry containing `isLoaded: true`, which would rehydrate and keep
      // suppressing the fetch; this drops the stale flags so they self-heal
      // without anyone having to clear site data. v2 adds `hubs`.
      version: 2,
      migrate: (persisted: any) => ({
        permissions: persisted?.permissions ?? [],
        roles: persisted?.roles ?? [],
        hubs: persisted?.hubs ?? [],
      }),
      merge: (persisted: any, current) => ({
        ...current,
        permissions: persisted?.permissions ?? [],
        roles: persisted?.roles ?? [],
        hubs: persisted?.hubs ?? [],
        // Always start a session needing a fresh fetch.
        isLoading: false,
        isLoaded: false,
        loadFailed: false,
      }),
    }
  )
);
